import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useAuth, useTheme } from '../App';
import { PageLayout } from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Checkbox } from '../components/ui/checkbox';
import { ScrollArea } from '../components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { cn } from '../lib/utils';
import { toast } from 'sonner';
import {
  Compass, Plus, Target, FolderKanban, Flag, Milestone as MilestoneIcon,
  Calendar, AlertTriangle, Lightbulb, Zap, Search, ChevronRight,
  ChevronDown, ChevronLeft, Users, BarChart3, Shield, Clock, Loader2, Trash2,
  Brain, CheckCircle2, XCircle, Edit2, Save, ArrowRight, GripVertical,
  Mic, MicOff, Filter, SortAsc, ArrowUpDown, PanelLeft, PanelRight,
  Settings2, ChevronUp
} from 'lucide-react';

const API = '/api';

const BP_TYPES = [
  { value: 'GOAL', label: 'Goal', icon: Target, color: 'bg-violet-500/10 text-violet-600 border-violet-200' },
  { value: 'OKR', label: 'OKR', icon: BarChart3, color: 'bg-blue-500/10 text-blue-600 border-blue-200' },
  { value: 'PROJECT', label: 'Project', icon: FolderKanban, color: 'bg-indigo-500/10 text-indigo-600 border-indigo-200' },
  { value: 'EPIC', label: 'Epic', icon: Zap, color: 'bg-purple-500/10 text-purple-600 border-purple-200' },
  { value: 'MILESTONE', label: 'Milestone', icon: Flag, color: 'bg-emerald-500/10 text-emerald-600 border-emerald-200' },
  { value: 'SPRINT', label: 'Sprint', icon: MilestoneIcon, color: 'bg-teal-500/10 text-teal-600 border-teal-200' },
  { value: 'EVENT', label: 'Event', icon: Calendar, color: 'bg-orange-500/10 text-orange-600 border-orange-200' },
  { value: 'RISK', label: 'Risk', icon: AlertTriangle, color: 'bg-red-500/10 text-red-600 border-red-200' },
  { value: 'DECISION', label: 'Decision', icon: Shield, color: 'bg-amber-500/10 text-amber-600 border-amber-200' },
  { value: 'IDEA', label: 'Idea', icon: Lightbulb, color: 'bg-yellow-500/10 text-yellow-600 border-yellow-200' },
  { value: 'HISTORICAL_EVENT', label: 'Historical', icon: Clock, color: 'bg-slate-500/10 text-slate-600 border-slate-200' },
];

const STATUSES = ['draft', 'planned', 'active', 'paused', 'completed', 'cancelled', 'at_risk'];
// Big-picture item states — mapped onto canonical task-state tokens so the
// whole app speaks the same colour vocabulary. Feb 2026 token migration.
const STATUS_COLORS = {
  draft:     'orka-state-open-soft',
  planned:   'orka-state-open-soft',
  active:    'orka-state-in-progress-soft',
  paused:    'orka-state-partial-soft',
  completed: 'orka-state-done-soft',
  cancelled: 'orka-state-cancelled-soft',
  at_risk:   'orka-state-blocked-soft',
};

const getTypeMeta = (type) => BP_TYPES.find(t => t.value === type) || BP_TYPES[9];

const ROADMAP_SCALES = [
  { id: 'week', label: 'Weeks' },
  { id: 'sprint', label: 'Sprints' },
  { id: 'month', label: 'Months' },
  { id: 'quarter', label: 'Quarters' },
  { id: 'half', label: 'Half Years' },
  { id: 'year', label: 'Years' },
];

const SORT_OPTIONS = [
  { id: 'date_asc', label: 'Date (earliest first)' },
  { id: 'date_desc', label: 'Date (latest first)' },
  { id: 'type', label: 'Type' },
  { id: 'status', label: 'Status' },
  { id: 'title', label: 'Title A-Z' },
];

// ==================== TIMELINE MATH HELPERS ====================

function getWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

function generateColumns(scale, centerDate, sprintConfig, count = 12) {
  const cols = [];
  const center = new Date(centerDate);
  const half = Math.floor(count / 2);

  if (scale === 'week') {
    for (let i = -half; i < count - half; i++) {
      const d = new Date(center);
      d.setDate(d.getDate() + i * 7 - center.getDay() + 1);
      const end = new Date(d); end.setDate(end.getDate() + 6);
      const wk = getWeekNumber(d);
      cols.push({ key: `w${d.getFullYear()}-${wk}`, label: `W${wk}`, sublabel: `${d.getDate()} ${d.toLocaleString('default',{month:'short'})}`, start: new Date(d), end });
    }
  } else if (scale === 'sprint' && sprintConfig?.duration) {
    const sprintStart = sprintConfig.start_date ? new Date(sprintConfig.start_date) : new Date(center.getFullYear(), 0, 6);
    const dur = sprintConfig.duration || 14;
    // Find the sprint nearest to center
    const diffDays = Math.floor((center - sprintStart) / 86400000);
    const currentSprintIdx = Math.floor(diffDays / dur);
    for (let i = currentSprintIdx - half; i < currentSprintIdx + count - half; i++) {
      const s = new Date(sprintStart); s.setDate(s.getDate() + i * dur);
      const e = new Date(s); e.setDate(e.getDate() + dur - 1);
      cols.push({ key: `sprint-${i+1}`, label: `Sprint ${i+1}`, sublabel: `${s.getDate()} ${s.toLocaleString('default',{month:'short'})}`, start: s, end: e });
    }
  } else if (scale === 'month') {
    for (let i = -half; i < count - half; i++) {
      const d = new Date(center.getFullYear(), center.getMonth() + i, 1);
      const end = new Date(d.getFullYear(), d.getMonth() + 1, 0);
      cols.push({ key: `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`, label: d.toLocaleString('default',{month:'short'}), sublabel: `${d.getFullYear()}`, start: d, end });
    }
  } else if (scale === 'quarter') {
    for (let i = -half; i < count - half; i++) {
      const q = ((center.getMonth() / 3) | 0) + i;
      const yr = center.getFullYear() + Math.floor(q / 4);
      const qn = ((q % 4) + 4) % 4;
      const d = new Date(yr, qn * 3, 1);
      const end = new Date(yr, qn * 3 + 3, 0);
      cols.push({ key: `${yr}-Q${qn+1}`, label: `Q${qn+1}`, sublabel: `${yr}`, start: d, end });
    }
  } else if (scale === 'half') {
    for (let i = -half; i < count - half; i++) {
      const hi = ((center.getMonth() < 6 ? 0 : 1)) + i;
      const yr = center.getFullYear() + Math.floor(hi / 2);
      const hn = ((hi % 2) + 2) % 2;
      const d = new Date(yr, hn * 6, 1);
      const end = new Date(yr, hn * 6 + 6, 0);
      cols.push({ key: `${yr}-H${hn+1}`, label: `H${hn+1}`, sublabel: `${yr}`, start: d, end });
    }
  } else { // year
    for (let i = -half; i < count - half; i++) {
      const yr = center.getFullYear() + i;
      cols.push({ key: `${yr}`, label: `${yr}`, sublabel: '', start: new Date(yr, 0, 1), end: new Date(yr, 11, 31) });
    }
  }
  return cols;
}

function getItemSpan(item, columns) {
  const ta = item.time_anchor || {};
  const startStr = ta.date;
  const endStr = ta.end_date;
  if (!startStr && !endStr) return null;

  const itemStart = startStr ? new Date(startStr) : null;
  const itemEnd = endStr ? new Date(endStr) : null;

  let firstCol = -1, lastCol = -1;
  for (let i = 0; i < columns.length; i++) {
    const col = columns[i];
    const inRange = (itemStart && itemStart <= col.end && (!itemEnd || itemEnd >= col.start)) ||
                    (itemEnd && itemEnd >= col.start && itemEnd <= col.end) ||
                    (itemStart && itemEnd && itemStart <= col.start && itemEnd >= col.end);
    if (inRange) {
      if (firstCol === -1) firstCol = i;
      lastCol = i;
    }
    // Single date, no end — contain to the column it fits in
    if (!itemEnd && itemStart && itemStart >= col.start && itemStart <= col.end) {
      firstCol = i; lastCol = i; break;
    }
  }
  if (firstCol === -1) return null;
  return { startCol: firstCol, span: lastCol - firstCol + 1 };
}

// ==================== ROADMAP COMPONENT ====================

const RoadmapView = ({ items, scale, onSelect, onAddToColumn, sprintConfig, filterTypes, filterWorkAreas, sortBy }) => {
  const scrollRef = useRef(null);
  const [centerDate] = useState(new Date());

  const columns = useMemo(() => {
    // ±24 months from today = 48 month columns. Proportional for other scales.
    const scaleCount = { week: 52, sprint: 26, month: 48, quarter: 16, half: 10, year: 6 };
    return generateColumns(scale, centerDate, sprintConfig, scaleCount[scale] || 48);
  }, [scale, centerDate, sprintConfig]);

  const filteredItems = useMemo(() => {
    let filtered = items.filter(i => {
      if (filterTypes.length > 0 && !filterTypes.includes(i.type)) return false;
      if (filterWorkAreas && filterWorkAreas.length > 0) {
        const itemAreas = i.work_area_ids || [];
        if (!filterWorkAreas.some(wa => itemAreas.includes(wa))) return false;
      }
      return true;
    });
    // Sort
    if (sortBy === 'date_desc') filtered.sort((a,b) => (b.time_anchor?.date || '').localeCompare(a.time_anchor?.date || ''));
    else if (sortBy === 'type') filtered.sort((a,b) => a.type.localeCompare(b.type));
    else if (sortBy === 'status') filtered.sort((a,b) => a.status.localeCompare(b.status));
    else if (sortBy === 'title') filtered.sort((a,b) => a.title.localeCompare(b.title));
    else filtered.sort((a,b) => (a.time_anchor?.date || '').localeCompare(b.time_anchor?.date || '')); // date_asc default
    return filtered;
  }, [items, filterTypes, filterWorkAreas, sortBy]);

  // Compute row layout: items placed in rows to avoid overlap
  const { rows, unscheduled } = useMemo(() => {
    const placed = [];
    const unsched = [];
    filteredItems.forEach(item => {
      const span = getItemSpan(item, columns);
      if (!span) { unsched.push(item); return; }
      // Find first row where this item fits (no overlap)
      let rowIdx = 0;
      while (true) {
        if (!placed[rowIdx]) { placed[rowIdx] = []; }
        const fits = !placed[rowIdx].some(p => {
          const pEnd = p.startCol + p.span;
          const iEnd = span.startCol + span.span;
          return span.startCol < pEnd && iEnd > p.startCol;
        });
        if (fits) break;
        rowIdx++;
      }
      if (!placed[rowIdx]) placed[rowIdx] = [];
      placed[rowIdx].push({ ...span, item });
    });
    return { rows: placed, unscheduled: unsched };
  }, [filteredItems, columns]);

  const todayColIdx = columns.findIndex(c => {
    const now = new Date();
    return now >= c.start && now <= c.end;
  });

  // Scroll to center on mount
  useEffect(() => {
    if (scrollRef.current && todayColIdx >= 0) {
      const colWidth = 160;
      const containerW = scrollRef.current.offsetWidth;
      scrollRef.current.scrollLeft = todayColIdx * colWidth - containerW / 2 + colWidth / 2;
    }
  }, [todayColIdx, columns]);

  const COL_W = 160;

  return (
    <div className="flex flex-col h-full">
      {/* Horizontal scrollable roadmap */}
      <div ref={scrollRef} className="flex-1 overflow-x-auto overflow-y-auto" data-testid="roadmap-scroll-container">
        <div style={{ minWidth: columns.length * COL_W + 'px', minHeight: '100%' }}>
          {/* Column headers */}
          <div className="flex sticky top-0 z-10 bg-card border-b">
            {columns.map((col, ci) => (
              <div
                key={col.key}
                style={{ width: COL_W, minWidth: COL_W }}
                className={cn(
                  "py-2 px-2 text-center border-r last:border-r-0 flex-shrink-0 cursor-pointer hover:bg-accent/50 transition-colors group/col relative",
                  ci === todayColIdx && "bg-violet-50 dark:bg-violet-500/5"
                )}
                onClick={() => onAddToColumn?.(col)}
                title="Click to add item"
                data-testid={`roadmap-col-${col.key}`}
              >
                <div className="text-xs font-semibold text-foreground">{col.label}</div>
                {col.sublabel && <div className="text-[10px] text-muted-foreground">{col.sublabel}</div>}
                {ci === todayColIdx && <div className="w-1.5 h-1.5 rounded-full bg-violet-500 mx-auto mt-1" />}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/col:opacity-100 transition-opacity pointer-events-none">
                  <Plus className="w-4 h-4 text-muted-foreground/60" />
                </div>
              </div>
            ))}
          </div>

          {/* Today marker line + rows */}
          <div className="relative" style={{ minHeight: 'calc(100vh - 14rem)' }}>
            {todayColIdx >= 0 && (
              <div className="absolute top-0 bottom-0 w-0.5 bg-violet-400/30 z-[5]"
                style={{ left: todayColIdx * COL_W + COL_W / 2 }} />
            )}

            {/* Grid lines — clickable empty areas */}
            <div className="absolute inset-0 flex">
              {columns.map((col, ci) => (
                <div key={col.key} style={{ width: COL_W, minWidth: COL_W }}
                  className={cn("border-r last:border-r-0 h-full cursor-pointer hover:bg-accent/20 transition-colors", ci === todayColIdx && "bg-violet-50/30 dark:bg-violet-500/3")}
                  onClick={() => onAddToColumn?.(col)}
                  title={`Add to ${col.label} ${col.sublabel || ''}`}
                />
              ))}
            </div>

            {/* Rows of items */}
            {rows.map((row, ri) => (
              <div key={ri} className="relative" style={{ height: 44 }}>
                {row.map(({ startCol, span, item }) => {
                  const meta = getTypeMeta(item.type);
                  const Icon = meta.icon;
                  return (
                    <div
                      key={item.item_id}
                      className={cn(
                        "absolute top-1 h-9 rounded-md border flex items-center gap-1.5 px-2 cursor-pointer orka-transition-colors",
                        "hover:shadow-md hover:z-20 z-10 overflow-hidden backdrop-blur-sm",
                        meta.color.replace('border-', 'border-').split(' ').slice(0,2).join(' '),
                        "bg-card/5 border-current/20"
                      )}
                      style={{
                        left: startCol * COL_W + 4,
                        width: span * COL_W - 8,
                      }}
                      onClick={(e) => { e.stopPropagation(); onSelect(item); }}
                      title={`${item.title} (${item.status})`}
                      data-testid={`roadmap-item-${item.item_id}`}
                    >
                      <Icon className="w-3.5 h-3.5 flex-shrink-0" />
                      <span className="text-xs font-medium truncate">{item.title}</span>
                      {span > 1 && (
                        <Badge className={cn("text-[8px] ml-auto flex-shrink-0", STATUS_COLORS[item.status])}>
                          {item.status}
                        </Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}

            {rows.length === 0 && (
              <div className="flex items-center justify-center py-16 text-muted-foreground relative z-10">
                <Compass className="w-6 h-6 mr-2 opacity-30" />
                <span className="text-sm">No items in this time range. Click anywhere to add.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Unscheduled items */}
      {unscheduled.length > 0 && (
        <div className="border-t p-3 bg-muted/20">
          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Unscheduled ({unscheduled.length})</h3>
          <div className="flex flex-wrap gap-2">
            {unscheduled.map(item => {
              const meta = getTypeMeta(item.type);
              const Icon = meta.icon;
              return (
                <div key={item.item_id} onClick={() => onSelect(item)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border bg-card cursor-pointer hover:shadow-sm text-xs orka-transition-colors">
                  <Icon className={cn("w-3 h-3", meta.color.split(' ')[1])} />
                  <span className="font-medium">{item.title}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== OKR TREE ====================

const OKRTreeNode = ({ item, level = 0, onSelect }) => {
  const [open, setOpen] = useState(level < 2);
  const meta = getTypeMeta(item.type);
  const Icon = meta.icon;
  const children = item.children_items || [];
  return (
    <div className={cn(level > 0 && "ml-5 border-l-2 border-border/50")}>
      <div className={cn("flex items-center gap-2 py-2 px-3 hover:bg-accent/50 rounded-lg cursor-pointer transition-colors", level === 0 && "bg-card border")}
        onClick={() => { if (children.length) setOpen(!open); onSelect(item); }} data-testid={`okr-node-${item.item_id}`}>
        {children.length > 0 ? (open ? <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />) : <div className="w-3.5" />}
        <div className={cn("w-6 h-6 rounded flex items-center justify-center border", meta.color)}><Icon className="w-3 h-3" /></div>
        <span className={cn("text-sm font-medium truncate flex-1", level === 0 ? "text-foreground" : "text-muted-foreground")}>{item.title}</span>
        <Badge className={cn("text-[10px]", STATUS_COLORS[item.status] || STATUS_COLORS.draft)}>{item.status}</Badge>
      </div>
      {open && children.map(child => <OKRTreeNode key={child.item_id} item={child} level={level + 1} onSelect={onSelect} />)}
    </div>
  );
};

// ==================== DETAIL PANEL ====================

const DetailPanel = ({ item, items, onClose, onDelete, onUpdate }) => {
  const meta = getTypeMeta(item.type);
  const Icon = meta.icon;
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});

  useEffect(() => {
    const ta = item.time_anchor || {};
    setForm({
      title: item.title, description: item.description, status: item.status,
      date: ta.date || '', end_date: ta.end_date || '',
      work_areas: (item.work_area_ids || []).join(', '),
      parent_id: item.parent_id || '', sprint_goal: item.sprint_goal || '',
      risk_level: item.risk_level || '', likelihood: item.likelihood || '',
      mitigation: item.mitigation || '', decision_record: item.decision_record || '',
    });
    setEditing(false);
  }, [item]);

  const handleSave = async () => {
    try {
      const body = { ...form, work_areas: form.work_area_ids ? form.work_area_ids.split(',').map(s => s.trim()) : [],
        time_anchor: { ...item.time_anchor, date: form.date || null, end_date: form.end_date || null } };
      const res = await fetch(`${API}/bigpicture/items/${item.item_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body)
      });
      if (res.ok) { toast.success('Updated'); onUpdate(); setEditing(false); }
    } catch { toast.error('Update failed'); }
  };

  return (
    <div className="w-96 border-l bg-card flex flex-col flex-shrink-0 h-full" data-testid="bp-detail-panel">
      <div className="p-4 border-b flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center border", meta.color)}><Icon className="w-4 h-4" /></div>
          <Badge className={cn("text-[10px] border", meta.color)}>{meta.label}</Badge>
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditing(!editing)}><Edit2 className="w-3.5 h-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => onDelete(item.item_id)}><Trash2 className="w-3.5 h-3.5" /></Button>
          <Button variant="ghost" size="icon" className="h-7 w-7" onClick={onClose}><XCircle className="w-4 h-4" /></Button>
        </div>
      </div>
      <ScrollArea className="flex-1 p-4">
        <div className="space-y-4">
          {editing ? (
            <>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Title</label>
                <Input value={form.title} onChange={e => setForm(f => ({...f, title: e.target.value}))} /></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Description</label>
                <Textarea value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} rows={4} /></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Status</label>
                <Select value={form.status} onValueChange={v => setForm(f => ({...f, status: v}))}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Start</label>
                  <Input type="date" value={form.date} onChange={e => setForm(f => ({...f, date: e.target.value}))} className="h-9 font-mono text-sm" /></div>
                <div><label className="text-xs font-medium text-muted-foreground mb-1 block">End</label>
                  <Input type="date" value={form.end_date} onChange={e => setForm(f => ({...f, end_date: e.target.value}))} className="h-9 font-mono text-sm" /></div>
              </div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Work Areas</label>
                <Input value={form.work_area_ids} onChange={e => setForm(f => ({...f, work_areas: e.target.value}))} /></div>
              <Button onClick={handleSave} className="w-full" size="sm"><Save className="w-3.5 h-3.5 mr-1.5" />Save</Button>
            </>
          ) : (
            <>
              <h2 className="text-lg font-semibold">{item.title}</h2>
              <Badge className={cn("text-xs", STATUS_COLORS[item.status])}>{item.status}</Badge>
              {item.description && <p className="text-sm text-muted-foreground leading-relaxed">{item.description}</p>}
              {(item.time_anchor?.date || item.time_anchor?.end_date) && (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Calendar className="w-3.5 h-3.5" />
                  <span className="font-mono">{item.time_anchor.date}{item.time_anchor.end_date && ` → ${item.time_anchor.end_date}`}</span>
                </div>
              )}
              {item.work_area_ids?.length > 0 && <div className="flex flex-wrap gap-1">{item.work_area_ids.map(w => <Badge key={w} variant="outline" className="text-[10px]">{w}</Badge>)}</div>}
              {item.sprint_goal && <div><h4 className="text-xs font-medium text-muted-foreground mb-1">Sprint Goal</h4><p className="text-sm">{item.sprint_goal}</p></div>}
              {item.risk_level && <div className="flex items-center gap-2"><AlertTriangle className="w-3.5 h-3.5 text-red-500" /><span className="text-sm">Risk: {item.risk_level} | Likelihood: {item.likelihood || 'N/A'}</span></div>}
              {item.mitigation && <div><h4 className="text-xs font-medium text-muted-foreground mb-1">Mitigation</h4><p className="text-sm">{item.mitigation}</p></div>}
              {item.decision_record && <div><h4 className="text-xs font-medium text-muted-foreground mb-1">Decision Record</h4><p className="text-sm">{item.decision_record}</p></div>}
            </>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

// ==================== SPRINT CONFIG ====================

const SprintConfigPanel = ({ config, onSave }) => {
  const [form, setForm] = useState({ duration: config?.duration || 14, start_date: config?.start_date || '', end_date: config?.end_date || '' });
  return (
    <Card className="mb-4">
      <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Settings2 className="w-4 h-4" />Sprint Configuration</CardTitle></CardHeader>
      <CardContent>
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Duration (days)</label>
            <Select value={String(form.duration)} onValueChange={v => setForm(f => ({...f, duration: parseInt(v)}))}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="7">1 week</SelectItem>
                <SelectItem value="14">2 weeks</SelectItem>
                <SelectItem value="21">3 weeks</SelectItem>
                <SelectItem value="28">4 weeks</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">Start Date</label>
            <Input type="date" value={form.start_date} onChange={e => setForm(f => ({...f, start_date: e.target.value}))} className="h-8 font-mono text-xs" data-testid="sprint-start-date" />
          </div>
          <div className="flex-1">
            <label className="text-xs font-medium text-muted-foreground mb-1 block">End Date</label>
            <Input type="date" value={form.end_date} onChange={e => setForm(f => ({...f, end_date: e.target.value}))} className="h-8 font-mono text-xs" data-testid="sprint-end-date" />
          </div>
          <Button size="sm" className="h-8" onClick={() => onSave(form)} data-testid="save-sprint-config"><Save className="w-3 h-3 mr-1" />Save</Button>
        </div>
      </CardContent>
    </Card>
  );
};

// ==================== MAIN PAGE ====================

const BigPicturePage = () => {
  const { theme, setTheme } = useTheme();
  const [view, setView] = useState('roadmap');
  const [items, setItems] = useState([]);
  const [okrTree, setOkrTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('');
  const [roadmapScale, setRoadmapScale] = useState('month');
  const [roadmapFilterTypes, setRoadmapFilterTypes] = useState([]);
  const [roadmapFilterWorkAreas, setRoadmapFilterWorkAreas] = useState([]);
  const [roadmapSort, setRoadmapSort] = useState('date_asc');

  // Create Item
  const [showCreate, setShowCreate] = useState(false);
  const [createPresetDate, setCreatePresetDate] = useState('');
  const defaultForm = { type: 'IDEA', title: '', description: '', status: 'draft', date: '', end_date: '', scope: 'personal', work_areas: '', parent_id: '', risk_level: '', likelihood: '', mitigation: '', decision_record: '', sprint_goal: '' };
  const [createForm, setCreateForm] = useState(defaultForm);

  // Task Dump
  const [showDump, setShowDump] = useState(false);
  const [dumpText, setDumpText] = useState('');
  const [dumpContext, setDumpContext] = useState('');
  const [dumpResult, setDumpResult] = useState(null);
  const [dumpLoading, setDumpLoading] = useState(false);
  const [selectedDumpTasks, setSelectedDumpTasks] = useState(new Set());
  const [isRecording, setIsRecording] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);

  // Detail & Sprint
  const [selectedItem, setSelectedItem] = useState(null);
  const [sprintConfig, setSprintConfig] = useState({ duration: 14, start_date: '', end_date: '' });
  const [capacity, setCapacity] = useState(null);

  // Add-to-column popup
  const [addColumnPopover, setAddColumnPopover] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType && filterType !== 'ALL') params.set('type', filterType);
      if (searchQuery) params.set('search', searchQuery);
      const res = await fetch(`${API}/bigpicture/items?${params}`, { credentials: 'include' });
      if (res.ok) setItems(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterType, searchQuery]);

  const fetchOKRTree = useCallback(async () => {
    try {
      const res = await fetch(`${API}/bigpicture/okr-tree`, { credentials: 'include' });
      if (res.ok) setOkrTree(await res.json());
    } catch {}
  }, []);

  const fetchCapacity = useCallback(async () => {
    const now = new Date();
    const start = now.toISOString().split('T')[0];
    const end = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
    try {
      const res = await fetch(`${API}/bigpicture/capacity?start_date=${start}&end_date=${end}`, { credentials: 'include' });
      if (res.ok) setCapacity(await res.json());
    } catch {}
  }, []);

  const fetchSprintConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API}/bigpicture/sprint-config`, { credentials: 'include' });
      if (res.ok) { const d = await res.json(); if (d?.duration) setSprintConfig(d); }
    } catch {}
  }, []);

  useEffect(() => { fetchItems(); fetchSprintConfig(); }, [fetchItems, fetchSprintConfig]);
  useEffect(() => { if (view === 'okr') fetchOKRTree(); }, [view, fetchOKRTree]);
  useEffect(() => { if (view === 'capacity') fetchCapacity(); }, [view, fetchCapacity]);

  const handleCreate = async () => {
    if (!createForm.title.trim()) { toast.error('Title required'); return; }
    try {
      const body = { ...createForm, work_areas: createForm.work_area_ids ? createForm.work_area_ids.split(',').map(s => s.trim()) : [] };
      const res = await fetch(`${API}/bigpicture/items`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify(body)
      });
      if (res.ok) { toast.success('Created'); fetchItems(); setShowCreate(false); setCreateForm(defaultForm); }
    } catch { toast.error('Failed to create'); }
  };

  const handleDelete = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await fetch(`${API}/bigpicture/items/${itemId}`, { method: 'DELETE', credentials: 'include' });
      toast.success('Deleted'); fetchItems(); setSelectedItem(null);
    } catch { toast.error('Failed'); }
  };

  const handleDump = async () => {
    if (!dumpText.trim()) { toast.error('Enter your thoughts first'); return; }
    setDumpLoading(true);
    try {
      const res = await fetch(`${API}/bigpicture/task-dump`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ raw_text: dumpText, context_id: dumpContext || undefined })
      });
      if (res.ok) {
        const data = await res.json();
        setDumpResult(data);
        const allIndices = new Set(data.generated_tasks.map((_, i) => i));
        setSelectedDumpTasks(allIndices);
      }
    } catch { toast.error('AI processing failed'); }
    finally { setDumpLoading(false); }
  };

  const handleApproveDump = async () => {
    if (!dumpResult || selectedDumpTasks.size === 0) return;
    try {
      const tasksToApprove = [...selectedDumpTasks].map(i => dumpResult.generated_tasks[i]);
      const res = await fetch(`${API}/bigpicture/task-dump/${dumpResult.dump_id}/approve`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ approved_indices: [...selectedDumpTasks], tasks: tasksToApprove })
      });
      if (res.ok) { toast.success(`${selectedDumpTasks.size} tasks created`); fetchItems(); setShowDump(false); setDumpResult(null); setDumpText(''); }
    } catch { toast.error('Failed'); }
  };

  const saveSprintConfig = async (config) => {
    try {
      const res = await fetch(`${API}/bigpicture/sprint-config`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify(config)
      });
      if (res.ok) { setSprintConfig(config); toast.success('Sprint config saved'); }
    } catch { toast.error('Failed to save'); }
  };

  // Voice recording for Task Dump
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setDumpLoading(true);
        try {
          const formData = new FormData();
          formData.append('audio', blob, 'dump.webm');
          const res = await fetch(`${API}/ai/transcribe`, { method: 'POST', credentials: 'include', body: formData });
          if (res.ok) { const d = await res.json(); setDumpText(prev => prev + (prev ? '\n' : '') + (d.text || '')); }
        } catch { toast.error('Transcription failed'); }
        finally { setDumpLoading(false); }
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch { toast.error('Microphone access denied'); }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  const handleColumnAdd = (col) => {
    setCreatePresetDate(col.start.toISOString().split('T')[0]);
    setAddColumnPopover(col);
  };

  const openCreateWithType = (type, presetDate) => {
    const endDate = addColumnPopover?.end?.toISOString().split('T')[0] || '';
    setCreateForm({ ...defaultForm, type, date: presetDate || createPresetDate, end_date: endDate });
    setShowCreate(true);
    setAddColumnPopover(null);
  };

  const allWorkAreas = useMemo(() => {
    const areas = new Set();
    items.forEach(i => (i.work_area_ids || []).forEach(wa => { if (wa) areas.add(wa); }));
    return Array.from(areas).sort();
  }, [items]);

  const viewItems = useMemo(() => {
    if (view === 'okr') return [];
    if (view === 'risks') return items.filter(i => ['RISK', 'DECISION'].includes(i.type));
    if (view === 'sprints') return items.filter(i => i.type === 'SPRINT');
    return items;
  }, [items, view]);

  const views = [
    { id: 'roadmap', label: 'Roadmap', icon: Compass },
    { id: 'okr', label: 'OKR Tree', icon: Target },
    { id: 'sprints', label: 'Sprints', icon: MilestoneIcon },
    { id: 'capacity', label: 'Capacity', icon: Users },
    { id: 'risks', label: 'Risks & Decisions', icon: AlertTriangle },
  ];

  const refreshAll = () => { fetchItems(); if (view === 'okr') fetchOKRTree(); if (view === 'capacity') fetchCapacity(); };

  return (
    <PageLayout
      title="Big Picture"
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowDump(true)} data-testid="task-dump-btn">
            <Brain className="w-4 h-4 mr-1.5" /> Task Dump
          </Button>
          <Button size="sm" onClick={() => { setCreateForm(defaultForm); setShowCreate(true); }} data-testid="create-bp-item-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Item
          </Button>
        </div>
      }
      theme={theme} setTheme={setTheme}
    >
      <div className="h-[calc(100vh-4rem)] flex flex-col overflow-hidden bg-background">

        {/* View Tabs + Roadmap Controls */}
        <div className="px-6 py-2 border-b bg-card/50 flex items-center gap-3 shrink-0 flex-wrap">
          <div className="flex gap-1 bg-muted p-0.5 rounded-lg">
            {views.map(v => (
              <button key={v.id} onClick={() => setView(v.id)}
                className={cn("flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md orka-transition-colors",
                  view === v.id ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"
                )} data-testid={`view-${v.id}`}>
                <v.icon className="w-3.5 h-3.5" /> {v.label}
              </button>
            ))}
          </div>

          {/* Roadmap-specific controls */}
          {view === 'roadmap' && (
            <div className="flex items-center gap-2 ml-2">
              {/* Scale selector */}
              <div className="flex gap-0.5 bg-muted p-0.5 rounded-md">
                {ROADMAP_SCALES.map(s => (
                  <button key={s.id} onClick={() => setRoadmapScale(s.id)}
                    className={cn("px-2 py-1 text-[10px] font-medium rounded orka-transition-colors",
                      roadmapScale === s.id ? "bg-violet-600 text-white shadow-sm" : "text-muted-foreground hover:text-foreground"
                    )} data-testid={`scale-${s.id}`}>
                    {s.label}
                  </button>
                ))}
              </div>

              {/* Filter by type */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 text-xs gap-1" data-testid="roadmap-filter-btn">
                    <Filter className="w-3 h-3" />Filter
                    {(roadmapFilterTypes.length + roadmapFilterWorkAreas.length) > 0 && <Badge variant="secondary" className="text-[9px] h-4 px-1 ml-1">{roadmapFilterTypes.length + roadmapFilterWorkAreas.length}</Badge>}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="start">
                  <div className="space-y-1">
                    <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2">Type</span>
                    {BP_TYPES.filter(t => t.value !== 'SPRINT').map(t => (
                      <label key={t.value} className="flex items-center gap-2 px-2 py-1 hover:bg-accent rounded text-xs cursor-pointer">
                        <Checkbox checked={roadmapFilterTypes.includes(t.value)}
                          onCheckedChange={checked => {
                            setRoadmapFilterTypes(prev => checked ? [...prev, t.value] : prev.filter(v => v !== t.value));
                          }} />
                        <t.icon className="w-3 h-3" />{t.label}
                      </label>
                    ))}
                    {allWorkAreas.length > 0 && (
                      <>
                        <div className="border-t my-1" />
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground px-2">Work Areas</span>
                        {allWorkAreas.map(wa => (
                          <label key={wa} className="flex items-center gap-2 px-2 py-1 hover:bg-accent rounded text-xs cursor-pointer">
                            <Checkbox checked={roadmapFilterWorkAreas.includes(wa)}
                              onCheckedChange={checked => {
                                setRoadmapFilterWorkAreas(prev => checked ? [...prev, wa] : prev.filter(v => v !== wa));
                              }} />
                            {wa}
                          </label>
                        ))}
                      </>
                    )}
                    {(roadmapFilterTypes.length > 0 || roadmapFilterWorkAreas.length > 0) && (
                      <Button variant="ghost" size="sm" className="w-full text-xs h-7" onClick={() => { setRoadmapFilterTypes([]); setRoadmapFilterWorkAreas([]); }}>Clear all</Button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Sort */}
              <Select value={roadmapSort} onValueChange={setRoadmapSort}>
                <SelectTrigger className="h-7 w-40 text-xs" data-testid="roadmap-sort">
                  <ArrowUpDown className="w-3 h-3 mr-1" /><SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SORT_OPTIONS.map(s => <SelectItem key={s.id} value={s.id} className="text-xs">{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="ml-auto flex items-center gap-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground" />
              <Input placeholder="Search..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8 h-8 w-48 text-xs" data-testid="bp-search" />
            </div>
            <Select value={filterType} onValueChange={v => setFilterType(v === 'ALL' ? '' : v)}>
              <SelectTrigger className="h-8 w-32 text-xs" data-testid="bp-type-filter"><SelectValue placeholder="All types" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">All types</SelectItem>
                {BP_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          <div className="flex-1 flex flex-col overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center py-20 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading...</div>
            ) : view === 'roadmap' ? (
              <>
                {roadmapScale === 'sprint' && (
                  <div className="px-4 pt-3">
                    <SprintConfigPanel config={sprintConfig} onSave={saveSprintConfig} />
                  </div>
                )}
                <RoadmapView
                  items={viewItems}
                  scale={roadmapScale}
                  sprintConfig={sprintConfig}
                  onSelect={setSelectedItem}
                  onAddToColumn={handleColumnAdd}
                  filterTypes={roadmapFilterTypes}
                  filterWorkAreas={roadmapFilterWorkAreas}
                  sortBy={roadmapSort}
                />
              </>
            ) : view === 'okr' ? (
              <ScrollArea className="flex-1 p-6">
                <div className="space-y-3">
                  {okrTree.length === 0 ? <EmptyState icon={Target} title="No goals yet" subtitle="Create a Goal, then add OKRs and Projects under it" /> :
                    okrTree.map(goal => <OKRTreeNode key={goal.item_id} item={goal} onSelect={setSelectedItem} />)}
                </div>
              </ScrollArea>
            ) : view === 'sprints' ? (
              <ScrollArea className="flex-1 p-6">
                <SprintConfigPanel config={sprintConfig} onSave={saveSprintConfig} />
                {viewItems.length === 0 ? <EmptyState icon={MilestoneIcon} title="No sprints yet" subtitle="Create sprint items in the Roadmap to track time-boxed work" /> : (
                  <div className="space-y-4 mt-4">
                    {viewItems.map(sprint => {
                      const ta = sprint.time_anchor || {};
                      // Find all items that overlap with this sprint's dates
                      const sprintStart = ta.date ? new Date(ta.date) : null;
                      const sprintEnd = ta.end_date ? new Date(ta.end_date) : null;
                      const containedItems = sprintStart && sprintEnd ? items.filter(i => {
                        if (i.item_id === sprint.item_id || i.type === 'SPRINT') return false;
                        const iDate = i.time_anchor?.date ? new Date(i.time_anchor.date) : null;
                        const iEnd = i.time_anchor?.end_date ? new Date(i.time_anchor.end_date) : null;
                        if (!iDate) return false;
                        // Item starts within sprint, or item spans across sprint
                        return (iDate >= sprintStart && iDate <= sprintEnd) ||
                               (iEnd && iEnd >= sprintStart && iDate <= sprintEnd);
                      }) : [];
                      return (
                        <Card key={sprint.item_id} className="overflow-hidden" data-testid={`sprint-card-${sprint.item_id}`}>
                          <div className="flex items-center justify-between px-4 py-3 bg-muted/30 border-b cursor-pointer" onClick={() => setSelectedItem(sprint)}>
                            <div className="flex items-center gap-2">
                              <MilestoneIcon className="w-4 h-4 text-violet-500" />
                              <span className="font-medium text-sm">{sprint.title}</span>
                              <Badge className={cn("text-[10px]", STATUS_COLORS[sprint.status])}>{sprint.status}</Badge>
                            </div>
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              {ta.date && <span className="font-mono">{ta.date}{ta.end_date && ` → ${ta.end_date}`}</span>}
                              <Badge variant="outline" className="text-[10px]">{containedItems.length} items</Badge>
                            </div>
                          </div>
                          {sprint.sprint_goal && (
                            <div className="px-4 py-2 border-b bg-violet-50/30 dark:bg-violet-950/10">
                              <p className="text-xs text-muted-foreground"><span className="font-medium">Goal:</span> {sprint.sprint_goal}</p>
                            </div>
                          )}
                          <div className="p-3">
                            {containedItems.length === 0 ? (
                              <p className="text-xs text-muted-foreground text-center py-3">No items scheduled in this sprint. Items with dates overlapping this sprint will appear here.</p>
                            ) : (
                              <div className="space-y-1">
                                {containedItems.map(ci => {
                                  const meta = getTypeMeta(ci.type);
                                  const Icon = meta.icon;
                                  const spansMultiple = ci.time_anchor?.end_date && sprintEnd && new Date(ci.time_anchor.end_date) > sprintEnd;
                                  return (
                                    <div key={ci.item_id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-accent/50 cursor-pointer text-xs" onClick={() => setSelectedItem(ci)}>
                                      <Icon className={cn("w-3.5 h-3.5 flex-shrink-0", meta.color.split(' ')[1])} />
                                      <span className="font-medium flex-1 truncate">{ci.title}</span>
                                      <Badge variant="outline" className="text-[9px]">{ci.type}</Badge>
                                      <Badge className={cn("text-[9px]", STATUS_COLORS[ci.status])}>{ci.status}</Badge>
                                      {spansMultiple && <Badge variant="secondary" className="text-[9px]">Multi-sprint</Badge>}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>
            ) : view === 'capacity' ? (
              <ScrollArea className="flex-1 p-6">
                {capacity ? (
                  <>
                    <div className="grid grid-cols-3 gap-4 mb-4">
                      <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold">{capacity.staff_count || 1}</p><p className="text-xs text-muted-foreground">Staff</p></CardContent></Card>
                      <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold">{capacity.total_holidays}</p><p className="text-xs text-muted-foreground">Holidays</p></CardContent></Card>
                      <Card><CardContent className="pt-4 text-center"><p className="text-2xl font-bold">{capacity.sprints}</p><p className="text-xs text-muted-foreground">Sprints</p></CardContent></Card>
                    </div>
                    <Card>
                      <CardHeader><CardTitle className="text-sm">Weekly Capacity (Next 90 Days)</CardTitle></CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {capacity.weeks?.map((w, i) => (
                            <div key={i} className="flex items-center gap-3 text-xs">
                              <span className="w-24 text-muted-foreground font-mono">{w.week_start}</span>
                              <div className="flex-1 h-5 bg-muted rounded-full overflow-hidden">
                                <div className={cn("h-full rounded-full", w.load_ratio > 1.5 ? "bg-red-500" : w.load_ratio > 1 ? "bg-amber-500" : "bg-emerald-500")}
                                  style={{ width: `${Math.min(100, w.load_ratio * 50)}%` }} />
                              </div>
                              <span className="w-16 text-right">{w.task_count} tasks</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </>
                ) : <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin mr-2" />Loading...</div>}
              </ScrollArea>
            ) : (
              <ScrollArea className="flex-1 p-6">
                <div className="bg-card rounded-xl border overflow-hidden">
                  <table className="w-full text-sm" data-testid="risks-table">
                    <thead className="bg-muted/50 border-b">
                      <tr>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Item</th>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Type</th>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Risk Level</th>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Status</th>
                        <th className="text-left px-4 py-2.5 font-medium text-muted-foreground text-xs">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {viewItems.length === 0 ? <tr><td colSpan={5} className="px-4 py-12 text-center text-muted-foreground text-xs">No risks or decisions recorded</td></tr> :
                        viewItems.map(item => (
                          <tr key={item.item_id} className="hover:bg-accent/50 cursor-pointer" onClick={() => setSelectedItem(item)}>
                            <td className="px-4 py-3 font-medium text-sm">{item.title}</td>
                            <td className="px-4 py-3"><Badge className={cn(getTypeMeta(item.type).color, "text-[10px] border")}>{item.type}</Badge></td>
                            <td className="px-4 py-3">{item.risk_level ? <Badge className={cn("text-[10px]", item.risk_level === 'high' ? 'bg-red-100 text-red-700' : item.risk_level === 'medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700')}>{item.risk_level}</Badge> : '-'}</td>
                            <td className="px-4 py-3"><Badge className={cn("text-[10px]", STATUS_COLORS[item.status])}>{item.status}</Badge></td>
                            <td className="px-4 py-3 text-xs text-muted-foreground font-mono">{item.time_anchor?.date || '-'}</td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Detail Panel */}
          {selectedItem && (
            <DetailPanel item={selectedItem} items={items} onClose={() => setSelectedItem(null)} onDelete={handleDelete}
              onUpdate={() => { refreshAll(); fetch(`${API}/bigpicture/items/${selectedItem.item_id}`, { credentials: 'include' }).then(r => r.ok ? r.json() : null).then(d => { if (d) setSelectedItem(d); }); }} />
          )}
        </div>
      </div>

      {/* Add-to-Column Popover */}
      {addColumnPopover && (
        <Dialog open={true} onOpenChange={() => setAddColumnPopover(null)}>
          <DialogContent className="max-w-xs">
            <DialogHeader>
              <DialogTitle className="text-sm">Add to {addColumnPopover.label} {addColumnPopover.sublabel}</DialogTitle>
              <DialogDescription className="text-xs">What would you like to add?</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-2 py-2">
              {BP_TYPES.filter(t => !['SPRINT', 'HISTORICAL_EVENT'].includes(t.value)).map(t => (
                <Button key={t.value} variant="outline" size="sm" className="justify-start text-xs h-9 gap-1.5"
                  onClick={() => openCreateWithType(t.value, createPresetDate)} data-testid={`add-${t.value.toLowerCase()}`}>
                  <t.icon className="w-3.5 h-3.5" />{t.label}
                </Button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* Create Item Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-base">Create Big Picture Item</DialogTitle>
            <DialogDescription>Add a strategic item to your planning horizon</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Type</label>
                <Select value={createForm.type} onValueChange={v => setCreateForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{BP_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Status</label>
                <Select value={createForm.status} onValueChange={v => setCreateForm(f => ({ ...f, status: v }))}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Title</label>
              <Input value={createForm.title} onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g., Q2 Revenue Target" data-testid="bp-title" /></div>
            <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Description</label>
              <Textarea value={createForm.description} onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))} placeholder="Details..." rows={3} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Start Date</label>
                <Input type="date" value={createForm.date} onChange={e => setCreateForm(f => ({ ...f, date: e.target.value }))} className="h-9 font-mono text-sm" /></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">End Date</label>
                <Input type="date" value={createForm.end_date} onChange={e => setCreateForm(f => ({ ...f, end_date: e.target.value }))} className="h-9 font-mono text-sm" /></div>
            </div>
            <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Work Areas</label>
              <Input value={createForm.work_area_ids} onChange={e => setCreateForm(f => ({ ...f, work_areas: e.target.value }))} placeholder="Engineering, Marketing" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Scope</label>
                <Select value={createForm.scope} onValueChange={v => setCreateForm(f => ({ ...f, scope: v }))}>
                  <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="personal">Personal</SelectItem><SelectItem value="org">Organisation</SelectItem></SelectContent>
                </Select></div>
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Parent Item</label>
                <Select value={createForm.parent_id || 'none'} onValueChange={v => setCreateForm(f => ({ ...f, parent_id: v === 'none' ? '' : v }))}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">None</SelectItem>
                    {items.filter(i => ['GOAL', 'OKR', 'PROJECT', 'EPIC'].includes(i.type)).map(i => <SelectItem key={i.item_id} value={i.item_id}>{i.type}: {i.title}</SelectItem>)}
                  </SelectContent>
                </Select></div>
            </div>
            {createForm.type === 'SPRINT' && (
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Sprint Goal</label>
                <Textarea value={createForm.sprint_goal} onChange={e => setCreateForm(f => ({ ...f, sprint_goal: e.target.value }))} placeholder="What do we want to achieve?" rows={2} /></div>
            )}
            {createForm.type === 'RISK' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Risk Level</label>
                    <Select value={createForm.risk_level} onValueChange={v => setCreateForm(f => ({...f, risk_level: v}))}>
                      <SelectTrigger className="h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                    </Select></div>
                  <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Likelihood</label>
                    <Select value={createForm.likelihood} onValueChange={v => setCreateForm(f => ({...f, likelihood: v}))}>
                      <SelectTrigger className="h-9"><SelectValue placeholder="Select" /></SelectTrigger>
                      <SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem></SelectContent>
                    </Select></div>
                </div>
                <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Mitigation</label>
                  <Textarea value={createForm.mitigation} onChange={e => setCreateForm(f => ({...f, mitigation: e.target.value}))} rows={2} /></div>
              </>
            )}
            {createForm.type === 'DECISION' && (
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Decision Record</label>
                <Textarea value={createForm.decision_record} onChange={e => setCreateForm(f => ({...f, decision_record: e.target.value}))} rows={3} placeholder="Context, options, decision, rationale..." /></div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={handleCreate} data-testid="confirm-create-bp">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Task Dump Dialog */}
      <Dialog open={showDump} onOpenChange={setShowDump}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Brain className="w-5 h-5 text-indigo-500" /> Task Dump</DialogTitle>
            <DialogDescription>Brain dump your thoughts — type or use voice — and let AI structure them into actionable tasks</DialogDescription>
          </DialogHeader>
          {!dumpResult ? (
            <div className="space-y-3 py-2">
              <div><label className="text-xs font-medium text-muted-foreground mb-1 block">Context (optional)</label>
                <Select value={dumpContext || 'none'} onValueChange={v => setDumpContext(v === 'none' ? '' : v)}>
                  <SelectTrigger className="h-9"><SelectValue placeholder="Select project/milestone..." /></SelectTrigger>
                  <SelectContent><SelectItem value="none">No context</SelectItem>
                    {items.filter(i => ['PROJECT', 'MILESTONE', 'EPIC', 'GOAL', 'SPRINT'].includes(i.type)).map(i => <SelectItem key={i.item_id} value={i.item_id}>{i.type}: {i.title}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-muted-foreground">Your thoughts</label>
                  <Button variant={isRecording ? "destructive" : "outline"} size="sm" className={cn("h-7 text-xs gap-1", isRecording && "animate-pulse")}
                    onClick={isRecording ? stopRecording : startRecording} data-testid="dump-voice-btn">
                    {isRecording ? <><MicOff className="w-3 h-3" />Stop Recording</> : <><Mic className="w-3 h-3" />Voice Input</>}
                  </Button>
                </div>
                <Textarea value={dumpText} onChange={e => setDumpText(e.target.value)}
                  placeholder="Type or speak your thoughts... e.g., For the Singapore Demo Day we need slides, investor deck, rehearsal, venue booking, press outreach..."
                  rows={6} data-testid="dump-text" />
              </div>
              <Button onClick={handleDump} disabled={dumpLoading || !dumpText.trim()} className="w-full" data-testid="dump-submit">
                {dumpLoading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Processing...</> : <><Brain className="w-4 h-4 mr-1.5" />Generate Tasks</>}
              </Button>
            </div>
          ) : (
            <div className="space-y-3 py-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">{dumpResult.generated_tasks.length} tasks generated</p>
                <Badge variant="outline" className="text-[10px]">{selectedDumpTasks.size} selected</Badge>
              </div>
              <ScrollArea className="max-h-[55vh]">
                <div className="space-y-3">
                  {dumpResult.generated_tasks.map((t, i) => (
                    <div key={i} className={cn("border rounded-lg p-3 space-y-2 transition-opacity", !selectedDumpTasks.has(i) && "opacity-40")} data-testid={`dump-task-${i}`}>
                      <div className="flex items-start gap-2">
                        <Checkbox className="mt-1" checked={selectedDumpTasks.has(i)} onCheckedChange={checked => {
                          setSelectedDumpTasks(prev => { const next = new Set(prev); if (checked) next.add(i); else next.delete(i); return next; });
                        }} />
                        <div className="flex-1 space-y-2">
                          <Input value={t.title} onChange={e => {
                            const updated = [...dumpResult.generated_tasks];
                            updated[i] = { ...updated[i], title: e.target.value };
                            setDumpResult({ ...dumpResult, generated_tasks: updated });
                          }} className="h-8 text-sm font-medium" />
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <label className="text-[10px] text-muted-foreground">Work Area</label>
                              <Input value={t.work_area_id || ''} onChange={e => {
                                const updated = [...dumpResult.generated_tasks];
                                updated[i] = { ...updated[i], work_area_id: e.target.value };
                                setDumpResult({ ...dumpResult, generated_tasks: updated });
                              }} className="h-7 text-xs" />
                            </div>
                            <div>
                              <label className="text-[10px] text-muted-foreground">Priority</label>
                              <Select value={String(t.priority || 2)} onValueChange={v => {
                                const updated = [...dumpResult.generated_tasks];
                                updated[i] = { ...updated[i], priority: parseInt(v) };
                                setDumpResult({ ...dumpResult, generated_tasks: updated });
                              }}>
                                <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="1">P1 - Critical</SelectItem>
                                  <SelectItem value="2">P2 - High</SelectItem>
                                  <SelectItem value="3">P3 - Medium</SelectItem>
                                  <SelectItem value="4">P4 - Low</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="text-[10px] text-muted-foreground">Effort</label>
                              <Select value={String(t.task_estimated_effort || 2)} onValueChange={v => {
                                const updated = [...dumpResult.generated_tasks];
                                updated[i] = { ...updated[i], task_estimated_effort: parseInt(v) };
                                setDumpResult({ ...dumpResult, generated_tasks: updated });
                              }}>
                                <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {[1,2,3,4,5].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <label className="text-[10px] text-muted-foreground">Suggested Date</label>
                              <Input type="date" value={t.suggested_date || ''} onChange={e => {
                                const updated = [...dumpResult.generated_tasks];
                                updated[i] = { ...updated[i], suggested_date: e.target.value };
                                setDumpResult({ ...dumpResult, generated_tasks: updated });
                              }} className="h-7 text-xs font-mono" />
                            </div>
                            <div>
                              <label className="text-[10px] text-muted-foreground">Description</label>
                              <Input value={t.description || ''} onChange={e => {
                                const updated = [...dumpResult.generated_tasks];
                                updated[i] = { ...updated[i], description: e.target.value };
                                setDumpResult({ ...dumpResult, generated_tasks: updated });
                              }} className="h-7 text-xs" placeholder="Optional details..." />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setDumpResult(null)} className="flex-1">Back</Button>
                <Button onClick={handleApproveDump} className="flex-1" data-testid="dump-approve">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" />Approve {selectedDumpTasks.size} Tasks
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </PageLayout>
  );
};

const EmptyState = ({ icon: Icon, title, subtitle }) => (
  <div className="text-center py-20 text-muted-foreground">
    <Icon className="w-10 h-10 mx-auto mb-3 opacity-30" />
    <p className="font-medium text-sm">{title}</p>
    <p className="text-xs mt-1 opacity-70">{subtitle}</p>
  </div>
);

export default BigPicturePage;
